"use client";

import React from "react";
import { Button } from "../../atoms/button/button";
import { GlowIndicator, type GlowVariant } from "../../atoms/glow-indicator/glow-indicator";
import styles from "./pagination.module.css";
import { withSafeRender } from "../../atoms/safe-render";

export interface PaginationProps {
  /** Current page index (0-based) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when next button is clicked */
  onNext?: () => void;
  /** Callback when previous button is clicked */
  onPrevious?: () => void;
  /** Label for previous button for accessibility */
  previousLabel?: string;
  /** Label for next button for accessibility */
  nextLabel?: string;
  /** Accessible name for the navigation region. Defaults to "Pages" */
  ariaLabel?: string;
  /** Names the previous arrow's click in reporting. Nothing is tracked without it. */
  prevLinkName?: string;
  /** Names the next arrow's click in reporting. Nothing is tracked without it. */
  nextLinkName?: string;
  /**
   * Where on the page this control sits, shared by both arrows — they live in
   * the same `<nav>`, so a per-arrow value would split one control's clicks
   * across two placements. Defaults to Button's own body position.
   */
  linkPosition?: string;
  /**
   * Identifies this pagination instance in reporting, shared by both arrows —
   * a section identifier such as "Channel Lineup", not a URL: neither arrow
   * renders an `<a>` here, so there is no destination to follow.
   */
  linkDestination?: string;
  /** Disable previous button when at first page */
  disablePrevious?: boolean;
  /** Disable next button when at last page */
  disableNext?: boolean;
  /** Variant for the progress indicator color. Defaults to "blue" */
  variant?: GlowVariant;
  /** Height of the glow blur effect in pixels. Defaults to 12 */
  glowBlurHeight?: number | string;
  /** Height of the glow bar element in pixels. Defaults to 4 */
  glowBarHeight?: number | string;
  /** CSS class name to apply to the glow indicator wrapper */
  glowIndicatorClassName?: string;
  /** URL for previous button icon when enabled */
  prevIconEnabled?: string;
  /** URL for previous button icon when disabled */
  prevIconDisabled?: string;
  /** URL for next button icon when enabled */
  nextIconEnabled?: string;
  /** URL for next button icon when disabled */
  nextIconDisabled?: string;
}

const DEFAULT_PREV_ICON_ENABLED = "https://www.directv.com/assets/content/dsu9ojsbzo/svg/icon-left-arrow-eclipse-neutral50.svg";
const DEFAULT_PREV_ICON_DISABLED = "https://www.directv.com/assets/content/oiydvklefb/svg/icon-left-arrow-eclipse-slate400.svg";

const DEFAULT_NEXT_ICON_ENABLED = "https://www.directv.com/assets/content/tfrdumw0o8/svg/icon-right-arrow-eclipse-neutral50.svg";
const DEFAULT_NEXT_ICON_DISABLED = "https://www.directv.com/assets/content/kw7jrirn0g/svg/icon-right-arrow-eclipse-slate400.svg";

function PrevIcon({
  disabled,
  enabledSrc,
  disabledSrc
}: {
  disabled: boolean;
  enabledSrc: string;
  disabledSrc: string;
}) {
  return (
    <img
      alt=""
      aria-hidden="true"
      src={disabled ? disabledSrc : enabledSrc}
      width={24}
      height={24}
    />
  );
}

function NextIcon({
  disabled,
  enabledSrc,
  disabledSrc
}: {
  disabled: boolean;
  enabledSrc: string;
  disabledSrc: string;
}) {
  return (
    <img
      alt=""
      aria-hidden="true"
      src={disabled ? disabledSrc : enabledSrc}
      width={24}
      height={24}
    />
  );
}

/**
 * Pagination component with navigation arrows and a glowing progress indicator.
 * Uses the GlowIndicator component to provide visual feedback for the current
 * page position in a series with variant-specific glow colors.
 */
function PaginationImpl({
  currentPage,
  totalPages,
  onNext,
  onPrevious,
  previousLabel,
  nextLabel,
  ariaLabel = "Pages",
  prevLinkName,
  nextLinkName,
  linkPosition,
  linkDestination,
  disablePrevious = false,
  disableNext = false,
  variant = "blue",
  glowBlurHeight = 12,
  glowBarHeight = 4,
  glowIndicatorClassName,
  prevIconEnabled = DEFAULT_PREV_ICON_ENABLED,
  prevIconDisabled = DEFAULT_PREV_ICON_DISABLED,
  nextIconEnabled = DEFAULT_NEXT_ICON_ENABLED,
  nextIconDisabled = DEFAULT_NEXT_ICON_DISABLED
}: PaginationProps) {
  const hasNoPages = totalPages <= 0;
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === totalPages - 1;

  const prevDisabled = disablePrevious || isFirstPage || hasNoPages;
  const nextDisabled = disableNext || isLastPage || hasNoPages;

  // Parse CSS length values (supports "12px", "1rem", or a plain number)
  const parseValue = (val: number | string | undefined): string => {
    if (typeof val === "number") {
      return `${val}px`;
    }
    return String(val || 0);
  };
  const buttonMarginTop = parseValue(glowBlurHeight);

  return (
    <nav className={styles.paginationContainer} aria-label={ariaLabel}>
      <div style={{ marginTop: buttonMarginTop }}>
        <Button
          variant="tertiary"
          size="small"
          onClick={onPrevious}
          disabled={prevDisabled}
          ariaLabel={previousLabel || "Previous page"}
          linkName={prevLinkName}
          linkPosition={linkPosition}
          linkDestination={linkDestination}
          startIcon={
            <PrevIcon
              disabled={prevDisabled}
              enabledSrc={prevIconEnabled}
              disabledSrc={prevIconDisabled}
            />
          }
          className={styles.navButton}
        />
      </div>

      <div
        className={styles.indicatorWrapper}
      >
        <div
          className={styles.glowWrapper}
          style={{ width: "100%" }}
        >
          <GlowIndicator
            variant={variant}
            width="100%"
            blurHeight={glowBlurHeight}
            barHeight={glowBarHeight}
            className={glowIndicatorClassName ? `${styles.glowIndicator} ${glowIndicatorClassName}` : styles.glowIndicator}
          />
        </div>
      </div>

      <div style={{ marginTop: buttonMarginTop }}>
        <Button
          variant="tertiary"
          size="small"
          onClick={onNext}
          disabled={nextDisabled}
          ariaLabel={nextLabel || "Next page"}
          linkName={nextLinkName}
          linkPosition={linkPosition}
          linkDestination={linkDestination}
          startIcon={
            <NextIcon
              disabled={nextDisabled}
              enabledSrc={nextIconEnabled}
              disabledSrc={nextIconDisabled}
            />
          }
          className={styles.navButton}
        />
      </div>
    </nav>
  );
}
export const Pagination = withSafeRender(PaginationImpl, "Pagination");

export default Pagination;
